// frontend/src/components/common/PullToRefresh.jsx
//
// Pull-to-refresh for the POG mobile PWA.
//
// PWAs in standalone display mode lose iOS Safari / Chrome's native
// pull-to-refresh gesture, so staff have no quick way to reload after
// a sync. This component restores it: a touch-driven gesture at the top
// of the page reveals a spinner; releasing past the threshold reloads
// the current page (bypassing service-worker cache).
//
// Active only on touch devices that don't have native pull-to-refresh
// (i.e. PWAs in standalone mode + plain mobile webviews). Desktop and
// non-standalone mobile browsers see no interception.
//
// Implementation notes:
//  - Listens to touchstart/touchmove/touchend on the window. Only acts
//    when scrollY === 0 at touchstart (avoids hijacking mid-page scroll).
//  - Pull distance is rubber-banded (eased) past the trigger threshold so
//    the gesture feels right.
//  - touchmove uses preventDefault past the threshold to suppress the
//    native overscroll bounce. Below the threshold we let the browser
//    scroll normally.
//  - On release past threshold: window.location.reload() — full reload
//    re-runs all hooks AND busts the service-worker cache via
//    `cache: 'reload'` semantics built into reload().
import { useEffect, useRef, useState } from 'react'
import { Box, CircularProgress } from '@mui/material'

const TRIGGER_DISTANCE = 80    // px the user has to drag before release triggers
const MAX_PULL = 140           // hard cap on visual pull distance
const RUBBER_BAND_K = 0.55     // 0..1 — lower = more resistance after threshold


function isStandaloneOrTouchOnly() {
  // Standalone PWA (iOS / Android) or anywhere without a hover-capable
  // pointing device — exactly the surfaces that lose native pull-to-refresh.
  if (typeof window === 'undefined') return false
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator?.standalone === true
  const isTouchOnly = window.matchMedia?.('(hover: none) and (pointer: coarse)')?.matches
  return Boolean(isStandalone || isTouchOnly)
}


export default function PullToRefresh({ children, onRefresh }) {
  // Visible pull distance (in px) — drives the spinner translateY.
  const [pull, setPull] = useState(0)
  // 'idle' → 'pulling' → 'releasing' (snap back) → 'idle'
  const [state, setState] = useState('idle')
  const startYRef = useRef(0)
  const activeRef = useRef(false)
  // Stash the latest enabled flag so the touchstart handler sees it
  // without re-binding listeners on every render.
  const enabledRef = useRef(false)

  useEffect(() => {
    enabledRef.current = isStandaloneOrTouchOnly()
  }, [])

  useEffect(() => {
    const handleTouchStart = (e) => {
      if (!enabledRef.current) return
      // Only intercept when at the top of the page; otherwise it's a
      // normal scroll gesture and we must stay out of the way.
      if (window.scrollY > 0) return
      if (e.touches.length !== 1) return
      startYRef.current = e.touches[0].clientY
      activeRef.current = true
    }

    const handleTouchMove = (e) => {
      if (!activeRef.current) return
      const dy = e.touches[0].clientY - startYRef.current
      if (dy <= 0) {
        // Finger went back up past the start — abort gesture.
        activeRef.current = false
        setPull(0)
        setState('idle')
        return
      }
      // Past the trigger, apply rubber-banding so the spinner doesn't
      // run away under the finger.
      let visiblePull = dy
      if (dy > TRIGGER_DISTANCE) {
        visiblePull = TRIGGER_DISTANCE + (dy - TRIGGER_DISTANCE) * RUBBER_BAND_K
      }
      visiblePull = Math.min(visiblePull, MAX_PULL)
      setPull(visiblePull)
      setState('pulling')
      // Suppress browser native overscroll once we've engaged the gesture.
      if (e.cancelable && dy > 8) e.preventDefault()
    }

    const handleTouchEnd = () => {
      if (!activeRef.current) return
      activeRef.current = false
      const finalPull = pull
      if (finalPull >= TRIGGER_DISTANCE) {
        setState('refreshing')
        // Hold the spinner at the trigger position briefly so the user
        // sees confirmation before the reload kicks in.
        setPull(TRIGGER_DISTANCE)
        setTimeout(() => {
          if (typeof onRefresh === 'function') onRefresh()
          else window.location.reload()
        }, 120)
      } else {
        // Snap back.
        setState('releasing')
        setPull(0)
        setTimeout(() => setState('idle'), 200)
      }
    }

    // passive: false is required so we can call preventDefault() in move.
    window.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('touchend', handleTouchEnd, { passive: true })
    window.addEventListener('touchcancel', handleTouchEnd, { passive: true })

    return () => {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
      window.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [pull, onRefresh])

  // Visual progress: 0 below trigger → 1 at trigger → 1 past trigger.
  const progress = Math.min(1, pull / TRIGGER_DISTANCE)
  const isAtTrigger = pull >= TRIGGER_DISTANCE
  const isRefreshing = state === 'refreshing'
  // Fade in once the user is past 30% of the trigger so the indicator
  // doesn't appear during incidental short flicks.
  const opacity = state === 'idle' ? 0 : Math.min(1, progress * 1.5)

  return (
    <>
      <Box
        aria-hidden={!isRefreshing && pull === 0}
        sx={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
          height: `${pull}px`,
          opacity,
          transition: state === 'releasing' ? 'height 200ms ease-out, opacity 200ms ease-out' : 'none',
          pointerEvents: 'none',
          zIndex: 1400,  // above MUI AppBar (1100), below modals (1300+ — reload races them anyway)
        }}
      >
        <Box
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 40, height: 40,
            mb: 1,
            borderRadius: '50%',
            background: 'background.paper',
            boxShadow: 2,
            transform: `rotate(${progress * 360}deg)`,
            transition: state === 'releasing' ? 'transform 200ms ease-out' : 'none',
          }}
        >
          <CircularProgress
            size={20}
            thickness={5}
            variant={isRefreshing ? 'indeterminate' : 'determinate'}
            value={isAtTrigger ? 100 : progress * 100}
            sx={{ color: isAtTrigger ? 'primary.main' : 'text.disabled' }}
          />
        </Box>
      </Box>
      {children}
    </>
  )
}
